import { React, Fragment, useEffect, useContext, useState, useRef } from 'react'
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';

import { AuthContext } from "../../utils/auth.js";
import { get, ref, update, set, query, startAt, endAt, orderByChild, onValue } from "firebase/database";
import { db } from '../../utils/firebaseApp.js';

import { PlusIcon, ChevronRightIcon, MapPinIcon } from '@heroicons/react/20/solid'
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Search } from 'lucide-react'

import toast from 'react-hot-toast';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { EditClassificationModal } from './Components/EditClassificationModal.js';

import ModalForYoutubeVideo from '../../components/utils/ModalForYoutubeVideo.js';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const statuses = {
    Complete: 'text-green-700 bg-green-50 ring-green-600/20',
    complete: 'text-green-700 bg-green-50 ring-green-600/20',
    'In progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    'in progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
    processing: 'text-gray-600 bg-gray-50 ring-gray-500/10',
    archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
    Archived: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
}

export default function ClassificationsList() {
    const { currentUser } = useContext(AuthContext);

    const [classifications, setClassifications] = useState([]);
    const [isLoadingClassifications, setIsLoadingClassifications] = useState(true);

    const location = useLocation();
    const history = useHistory();

    const adminEmails = ['barzoiu.alexandru@gmail.com', "alexraducu03@gmail.com"];
    const isAdmin = adminEmails.includes(currentUser.email);

    const [showEditClassificationModal, setShowEditClassificationModal] = useState(false);
    const [currentClassificationData, setCurrentClassificationData] = useState(null);

    const [adminUserId, setAdminUserId] = useState('');
    const [searchedUserId, setSearchedUserId] = useState('');

    // Load admin user ID from URL on mount/change
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const adminUserIdParam = queryParams.get('adminUserId');

        if (isAdmin && adminUserIdParam) {
            setSearchedUserId(adminUserIdParam);
            setAdminUserId(adminUserIdParam);
        }
    }, [isAdmin, location.search]);

    useEffect(() => {
        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : currentUser.uid;
        let userId = targetUserId;

        // Maintain listeners for real-time updates
        const classificationsRef = ref(db, `document_classification/classifications/${userId}`);
        const batchesRef = ref(db, `document_classification/batches/${userId}`);
        setIsLoadingClassifications(true);

        // Store unsubscribe functions
        let batchesListener = () => { };
        const classificationsListener = onValue(classificationsRef, (classificationsSnapshot) => {
            const classificationsData = classificationsSnapshot.val();

            batchesListener();
            batchesListener = onValue(batchesRef, (batchesSnapshot) => {
                const batchesData = batchesSnapshot.val();

                if (classificationsData) {
                    const tempClassifications = Object.keys(classificationsData).map((classificationId) => {
                        const classification = classificationsData[classificationId];
                        const classificationBatches = batchesData && batchesData[classificationId] ? batchesData[classificationId] : {};

                        // Determine the classification status based on batches
                        let classificationStatus = 'complete';
                        if (classificationBatches) {
                            Object.values(classificationBatches).forEach((batch) => {
                                if (batch.status === 'processing') {
                                    classificationStatus = 'processing';
                                }
                            });
                        }

                        return {
                            name: classification.name ?? 'undefined',
                            description: classification.description ?? '',
                            status: classificationStatus,
                            documentTypes: classification.documentTypes ?? {},
                            noOfBatches: classificationBatches ? Object.keys(classificationBatches).length : 0,
                            batches: classificationBatches,
                            id: classificationId,
                            createdAt: classification.createdAt ?? Date.now(),
                            pinned: classification.pinned ?? false,
                        };
                    });

                    let sortedClassifications = tempClassifications.sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
                    sortedClassifications = sortedClassifications.sort((a, b) => Number(b.pinned) - Number(a.pinned));

                    setClassifications(sortedClassifications);
                } else {
                    setClassifications([]);
                }
                setIsLoadingClassifications(false);
            }, () => {
                setIsLoadingClassifications(false);
            });
        }, () => {
            setIsLoadingClassifications(false);
        });

        // Cleanup listeners on component unmount
        return () => {
            classificationsListener();
            batchesListener();
        };
    }, [currentUser.uid, searchedUserId, isAdmin, location.search, history]);

    const [open, setOpen] = useState(false);

    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const environment = process.env.REACT_APP_ENV;

    const deleteClassification = async (classificationId) => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/documentClassification/deleteClassification";

            await axios.delete(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                },
                data: {
                    classificationId: classificationId
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Classification deleted successfully");
                } else {
                    toast.error("Classification deletion failed");
                }
            }).catch((error) => {
                toast.error("Classification deletion failed");
            });
        });
    }

    const pinClassification = async (classificationId) => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/documentClassification/pinClassification";

            await axios.post(url, {
                "classificationId": classificationId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Classification pinned successfully");
                } else {
                    toast.error("Classification pinning failed");
                }
            }).catch((error) => {
                toast.error("Classification pinning failed: " + error.response.data);
            });
        });
    }

    const unpinClassification = async (classificationId) => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/documentClassification/unpinClassification";

            await axios.post(url, {
                "classificationId": classificationId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then((response) => {
                if (response.status === 200) {
                    toast.success("Classification unpinned successfully");
                } else {
                    toast.error("Classification unpinning failed");
                }
            }).catch((error) => {
                toast.error("Classification unpinning failed");
            });
        });
    }

    const getLinkedTemplates = (classification) => {
        return classification.documentTypes.map((documentType) => {
            if (documentType.extractionId) {
                return 1;
            }
            return 0;
        }).reduce((a, b) => a + b, 0);
    }

    const handleAdminSearch = () => {
        if (adminUserId.trim()) {
            setSearchedUserId(adminUserId.trim());
            // Add adminUserId to URL
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('adminUserId', adminUserId.trim());
            history.replace(`${location.pathname}?${queryParams.toString()}`);
        }
    };

    const handleAdminSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAdminSearch();
        }
    };

    const handleClearSearch = () => {
        setAdminUserId('');
        setSearchedUserId('');
        // Remove adminUserId from URL
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete('adminUserId');
        const newUrl = queryParams.toString()
            ? `${location.pathname}?${queryParams.toString()}`
            : location.pathname;
        history.replace(newUrl);
    };

    const [showVideo, setShowVideo] = useState(false);
    const [showWelcomeToast, setShowWelcomeToast] = useState(false);
    const [accountObject, setAccountObject] = useState(null);

    const updateAccountObject = async () => {
        // Reference to the user's database path
        const databaseRef = ref(db, 'userDetails/' + currentUser.uid + '/account');

        const updatedUserDetails = {
            classificationVideo: true,
        };

        // Set the data in the database
        try {
            await update(databaseRef, updatedUserDetails);
        } catch (error) {
            console.error("Error updating data:", error);
        }
    }

    const fetchUserDetails = async () => {
        const databaseRef = ref(db, 'userDetails/' + currentUser.uid + '/account');

        try {
            const snapshot = await get(databaseRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                setAccountObject(data);

                let welcomeVideo = data.classificationVideo;
                if (welcomeVideo !== undefined && welcomeVideo !== null) {
                    setShowVideo(false);
                    setShowWelcomeToast(true);
                } else {
                    setShowVideo(true);
                    updateAccountObject();
                }
            } else {
                console.log("No data available");
                setAccountObject({});
                setShowVideo(true);
            }
        } catch (error) {
            // console.error("Error fetching data:", error);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            await fetchUserDetails();
        }

        fetchData();
    }, [currentUser.uid]);

    return (
        <div>
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Classifications</h1>
                    <p className="mt-1 text-sm text-gray-700">
                        A list of all the classifications that have been created.
                    </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <button
                        onClick={() => setShowEditClassificationModal(true)}
                        className="ml-2 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                        New classification
                    </button>
                </div>
            </div>

            {/* Admin Search */}
            {isAdmin && (
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                            Admin
                        </span>
                        <h3 className="text-sm font-semibold text-indigo-900">View classifications for any user</h3>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={adminUserId}
                            onChange={(e) => setAdminUserId(e.target.value)}
                            onKeyPress={handleAdminSearchKeyPress}
                            placeholder="Enter User ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleAdminSearch}
                            disabled={!adminUserId.trim()}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                        {searchedUserId && (
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {searchedUserId && (
                        <p className="mt-2 text-sm text-indigo-700">
                            Viewing classifications for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                        </p>
                    )}
                </div>
            )}

            {showWelcomeToast && (
                <ShowWelcomeMessage setShowWelcomeToast={setShowWelcomeToast} setShowVideo={setShowVideo} />
            )}

            <ModalForYoutubeVideo open={showVideo} setOpen={setShowVideo} >
                <ContainerForYoutubeVideo
                    closeModal={() => {
                        setShowVideo(false);
                        setShowWelcomeToast(true);
                    }}
                    updateAccountObject={updateAccountObject}
                />
            </ModalForYoutubeVideo>

            <EditClassificationModal
                open={showEditClassificationModal}
                onOpenChange={setShowEditClassificationModal}
                currentClassificationData={currentClassificationData}
            />

            <ul className="divide-y mt-6 divide-gray-100 overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
                {isLoadingClassifications && (
                    Array.from({ length: 3 }).map((_, index) => (
                        <li key={`classification-skeleton-${index}`} className="px-4 py-5 sm:px-6">
                            <div className="flex justify-between gap-x-6">
                                <div className="min-w-0 flex-auto">
                                    <Skeleton width={180} height={20} />
                                    <div className="mt-2">
                                        <Skeleton width={280} height={14} />
                                    </div>
                                </div>
                                <Skeleton circle width={20} height={20} />
                            </div>
                        </li>
                    ))
                )}

                {!isLoadingClassifications && classifications.map((classification) => {
                    // Preserve adminUserId in navigation URL
                    const classificationUrl = searchedUserId
                        ? `/classifications/${classification.id}?adminUserId=${searchedUserId}`
                        : `/classifications/${classification.id}`;

                    return (
                        <li key={classification.id} className="cursor-pointer gap-x-6 hover:bg-gray-50">
                            <a href={classificationUrl} className='px-4 py-5 sm:px-6 relative flex justify-between'>
                                <div className="flex min-w-0 gap-x-4">
                                    <div className="min-w-0 flex-auto">
                                        <div className="flex items-start">
                                            {classification.pinned && (
                                                <p className="text-sm font-semibold leading-6 text-gray-900 mr-2">
                                                    <MapPinIcon className="h-5 w-5 text-gray-400" />
                                                </p>
                                            )}
                                            <p className="text-sm font-semibold leading-6 text-gray-900 mr-3">
                                                {classification.name}
                                            </p>
                                            <p className={classNames(
                                                statuses[classification.status],
                                                'rounded-md whitespace-nowrap mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                                            )}>
                                                {classification.status}
                                            </p>
                                        </div>
                                        <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                                            {(classification.description !== null && classification.description.length > 0) &&
                                                <>
                                                    <p className="whitespace-nowrap">
                                                        {classification.description}
                                                    </p>
                                                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                        <circle cx={1} cy={1} r={1} />
                                                    </svg>
                                                </>
                                            }
                                            <p className="truncate">
                                                {classification.documentTypes.length} Document {classification.documentTypes.length === 1 ? 'Type' : 'Types'}
                                            </p>
                                            <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                <circle cx={1} cy={1} r={1} />
                                            </svg>
                                            <p className="truncate">
                                                {getLinkedTemplates(classification)} Linked Extraction {getLinkedTemplates(classification) === 1 ? 'Template' : 'Templates'}
                                            </p>
                                            {environment === 'development' && classification.workflowId &&
                                                <>
                                                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                                                        <circle cx={1} cy={1} r={1} />
                                                    </svg>
                                                    <p>workflowId: {classification.workflowId}</p>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-x-4">
                                    <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                                </div>
                            </a>
                        </li>
                    );
                })}
            </ul>

            {!isLoadingClassifications && classifications.length === 0 &&
                <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900">No classifications yet</h2>
                        <p className="mt-1.5 text-sm text-gray-600">
                            Get started by creating a new classification.
                        </p>
                    </div>
                </div>
            }
        </div>
    )
}

function ShowWelcomeMessage({ setShowWelcomeToast, setShowVideo }) {
    return (
        <>
            <div className="z-100 pointer-events-none fixed inset-x-0 bottom-0 sm:flex sm:justify-center sm:px-6 sm:pb-5 lg:px-8">
                <div className="pointer-events-auto flex items-center justify-between gap-x-6 bg-gray-900 px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5">
                    <p className="text-sm leading-6 text-white">
                        <button
                            onClick={() => {
                                setShowWelcomeToast(false);
                                setShowVideo(true);
                            }}
                        >
                            <strong className="font-semibold">New Feature: Document Classification!</strong>
                            <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
                                <circle cx={1} cy={1} r={1} />
                            </svg>
                            Upload files and let AI automatically identify document types
                            <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
                                <circle cx={1} cy={1} r={1} />
                            </svg>
                            Watch demo&nbsp;<span aria-hidden="true">&rarr;</span>
                        </button>
                    </p>
                    <button
                        type="button"
                        className="-m-1.5 flex-none p-1.5"
                        onClick={() => {
                            setShowWelcomeToast(false);
                        }}
                    >
                        <span className="sr-only">Dismiss</span>
                        <XMarkIcon className="h-5 w-5 text-white" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </>
    )
}

const ContainerForYoutubeVideo = ({ closeModal, updateAccountObject }) => {
    const iframeRef = useRef(null);

    const toggleFullscreen = () => {
        if (iframeRef.current) {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                iframeRef.current.requestFullscreen().catch(e => {
                    console.log("Error attempting to enable full-screen mode:", e);
                });
            }
        }
    };

    const skipFunction = () => {
        updateAccountObject();
        closeModal();
    }

    const watchFunction = () => {
        updateAccountObject();
        toggleFullscreen();
    }

    return (
        <div className="modal-container" style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: '20px 20px 20px 20px', // Space between the border and content
        }}>
            <h1 className="mt-2 text-2xl font-semibold text-gray-900">
                New Feature: Document Classification! 🚀
            </h1>
            {/* add secondary small text */}
            <p className="mb-3.5 text-base text-gray-500">
                Upload files and let AI automatically classify each document into predefined document types. Watch the demo below to see how it works.
            </p>
            <iframe
                ref={iframeRef}
                width="100%"
                height="95%" // Adjust the height based on your requirements
                src="https://www.youtube.com/embed/UA6Nf5QovGU?autoplay=1&controls=1"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="Document Classification Demo - Extracta LABS"
                style={{ marginBottom: '10px', borderRadius: '5px' }}
            ></iframe>
            <div className="mt-4 flex items-center justify-end">
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => skipFunction()}
                >
                    Skip
                </button>
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                    onClick={() => watchFunction()}
                >
                    <ArrowRightIcon className="-ml-0.5 mr-1 h-5 w-5" aria-hidden="true" />
                    Watch Demo
                </button>
            </div>
        </div>
    );
}